import { useEffect, useState } from 'react';
import {
  Box,
  TextInput,
  Group,
  Button,
  Select,
  NumberInput,
  Paper,
  Stack,
  Text,
  ActionIcon,
  Collapse,
} from '@mantine/core';
// import { DatePickerInput } from '@mantine/dates';
import { FaSearch, FaFilter, FaTimes, FaChevronUp } from 'react-icons/fa';
import { InvoiceSearchFilters, InvoiceStatus } from '../../types';

interface InvoiceSearchBarProps {
  filters: InvoiceSearchFilters;
  onFiltersChange: (filters: InvoiceSearchFilters) => void;
  onSearch: () => void;
  onClear: () => void;
  loading?: boolean;
}

const statusOptions = [
  { value: InvoiceStatus.ISSUED, label: 'Issued' },
  { value: InvoiceStatus.CONFIRMED_BY_CLIENT, label: 'Confirmed' },
  { value: InvoiceStatus.PAID, label: 'Paid' },
  { value: InvoiceStatus.PARTIALLY_PAID, label: 'Partially Paid' },
  { value: InvoiceStatus.CANCELLED, label: 'Cancelled' },
];

const currencyOptions = [
  { value: 'USD', label: 'USD' },
  { value: 'EUR', label: 'EUR' },
  { value: 'GBP', label: 'GBP' },
  { value: 'BTC', label: 'BTC' },
];

type QuickSearchPatch = Pick<
  InvoiceSearchFilters,
  'invoiceNumber' | 'clientName' | 'clientEmail'
>;

const isoDay = (d: Date | undefined): string =>
  d && !Number.isNaN(d.getTime()) ? d.toISOString().split('T')[0] : '';

// These are free-text date fields, so every partial value a person types on the way
// to a real date passes through here. `new Date('2026-0')` is an Invalid Date, and
// rendering one threw RangeError and took the whole search bar down mid-keystroke.
const parseDay = (value: string): Date | undefined => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return undefined;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? undefined : d;
};

export function quickSearchPatch(value: string): QuickSearchPatch {
  const term = value.trim();
  const empty: QuickSearchPatch = {
    invoiceNumber: undefined,
    clientName: undefined,
    clientEmail: undefined,
  };
  if (!term) return empty;
  if (/^(cpf|test|inv)-|^\d{4,}$/i.test(term))
    return { ...empty, invoiceNumber: value };
  if (term.includes('@')) return { ...empty, clientEmail: value };
  return { ...empty, clientName: value };
}

export function InvoiceSearchBar({
  filters,
  onFiltersChange,
  onSearch,
  onClear,
  loading = false,
}: InvoiceSearchBarProps) {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [localFilters, setLocalFilters] =
    useState<InvoiceSearchFilters>(filters);

  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  const applyFilters = (patch: Partial<InvoiceSearchFilters>) => {
    const newFilters = { ...localFilters, ...patch };
    setLocalFilters(newFilters);
    onFiltersChange(newFilters);
  };

  const handleFilterChange = (
    field: keyof InvoiceSearchFilters,
    value: any,
  ) => {
    applyFilters({ [field]: value });
  };

  const handleClear = () => {
    const emptyFilters: InvoiceSearchFilters = {};
    setLocalFilters(emptyFilters);
    onFiltersChange(emptyFilters);
    onClear();
  };

  const hasActiveFilters = Object.keys(localFilters).some(
    (key) =>
      localFilters[key as keyof InvoiceSearchFilters] !== undefined &&
      localFilters[key as keyof InvoiceSearchFilters] !== null &&
      localFilters[key as keyof InvoiceSearchFilters] !== '',
  );

  return (
    <Box>
      <Group justify="space-between" align="flex-end" mb="md">
        <Group style={{ flex: 1 }}>
          <TextInput
            placeholder="Search by invoice number, payer, or email..."
            leftSection={<FaSearch size={14} />}
            value={
              localFilters.invoiceNumber ||
              localFilters.clientName ||
              localFilters.clientEmail ||
              ''
            }
            onChange={(e) => applyFilters(quickSearchPatch(e.target.value))}
            style={{ minWidth: 300 }}
          />

          <ActionIcon
            variant={showAdvanced ? 'filled' : 'light'}
            onClick={() => setShowAdvanced(!showAdvanced)}
            title="Advanced filters"
          >
            <FaFilter size={14} />
          </ActionIcon>
        </Group>

        <Group>
          <Button
            variant="light"
            onClick={onSearch}
            loading={loading}
            leftSection={<FaSearch size={14} />}
          >
            Search
          </Button>

          {hasActiveFilters && (
            <Button
              variant="subtle"
              onClick={handleClear}
              leftSection={<FaTimes size={14} />}
            >
              Clear
            </Button>
          )}
        </Group>
      </Group>

      <Collapse expanded={showAdvanced}>
        <Paper withBorder p="md" mb="md">
          <Stack gap="md">
            <Group justify="space-between" align="center">
              <Text size="sm" fw={500}>
                Advanced Filters
              </Text>
              <ActionIcon
                variant="subtle"
                size="sm"
                onClick={() => setShowAdvanced(false)}
              >
                <FaChevronUp size={12} />
              </ActionIcon>
            </Group>

            <Group>
              <TextInput
                label="Invoice Number"
                placeholder="CPF-0110263"
                value={localFilters.invoiceNumber || ''}
                onChange={(e) =>
                  handleFilterChange('invoiceNumber', e.target.value)
                }
                style={{ minWidth: 200 }}
              />

              <TextInput
                label="Client Name"
                placeholder="Company Name"
                value={localFilters.clientName || ''}
                onChange={(e) =>
                  handleFilterChange('clientName', e.target.value)
                }
                style={{ minWidth: 200 }}
              />

              <TextInput
                label="Client Email"
                placeholder="client@company.com"
                value={localFilters.clientEmail || ''}
                onChange={(e) =>
                  handleFilterChange('clientEmail', e.target.value)
                }
                style={{ minWidth: 200 }}
              />
            </Group>

            <Group>
              <Select
                label="Status"
                placeholder="Select status"
                data={statusOptions}
                value={localFilters.status || null}
                onChange={(value) => handleFilterChange('status', value)}
                clearable
                style={{ minWidth: 150 }}
              />

              <NumberInput
                label="Amount"
                placeholder="0.00"
                value={localFilters.amount || ''}
                onChange={(value: string | number) =>
                  handleFilterChange(
                    'amount',
                    typeof value === 'string'
                      ? parseFloat(value) || undefined
                      : value,
                  )
                }
                min={0}
                step={0.01}
                decimalScale={2}
                style={{ minWidth: 150 }}
              />

              <Select
                label="Currency"
                placeholder="Select currency"
                data={currencyOptions}
                value={localFilters.currency || null}
                onChange={(value: string | null) =>
                  handleFilterChange('currency', value)
                }
                clearable
                style={{ minWidth: 120 }}
              />
            </Group>

            <Group>
              <TextInput
                label="Date From"
                placeholder="YYYY-MM-DD"
                value={isoDay(localFilters.dateFrom)}
                onChange={(e) =>
                  handleFilterChange('dateFrom', parseDay(e.target.value))
                }
                style={{ minWidth: 200 }}
              />

              <TextInput
                label="Date To"
                placeholder="YYYY-MM-DD"
                value={isoDay(localFilters.dateTo)}
                onChange={(e) =>
                  handleFilterChange('dateTo', parseDay(e.target.value))
                }
                style={{ minWidth: 200 }}
              />
            </Group>
          </Stack>
        </Paper>
      </Collapse>
    </Box>
  );
}

export default InvoiceSearchBar;
